import { ActionType } from 'typesafe-actions';

import * as actions from '../actions/accountActions';
import { getKnoOrganizationFunctionsGroupStoreKey } from '../common/getKnoOrganizationFunctionsGroupStoreKey';
import { getTemplatesGroupStoreKey } from '../common/getTemplatesGroupStoreKey';
import { initialAccountState } from '../constants/initialAccountState';
import { AccountActionsEnum } from '../enums/AccountActionsEnum';
import { AccountStatusesEnum } from '../enums/AccountStatusesEnum';
import { IAccountState } from '../interfaces/IAccountState';

export type AccountAction = ActionType<typeof actions>;

export function accountReducer(state = initialAccountState, action: AccountAction) {
  switch (action.type) {
    case AccountActionsEnum.ACCOUNT_CHECKLIST_TEMPLATES_GET_REQUEST: {
      const { params } = action.payload;
      const templatesGroupKey: string = getTemplatesGroupStoreKey(params);
      const newState: IAccountState = {
        ...state,
        checklistTemplates: {
          ...state.checklistTemplates,
          [templatesGroupKey]: {
            ...state.checklistTemplates[templatesGroupKey],
            status: AccountStatusesEnum.LOADING,
          },
        },
      };

      return newState;
    }
    case AccountActionsEnum.ACCOUNT_CHECKLIST_TEMPLATES_GET_SUCCESS: {
      const { params, data } = action.payload;
      const templatesGroupKey: string = getTemplatesGroupStoreKey(params);
      const newState: IAccountState = {
        ...state,
        checklistTemplates: {
          ...state.checklistTemplates,
          [templatesGroupKey]: {
            ...state.checklistTemplates[templatesGroupKey],
            data,
            status: AccountStatusesEnum.SUCCEEDED,
          },
        },
      };

      return newState;
    }
    case AccountActionsEnum.ACCOUNT_CHECKLIST_TEMPLATES_GET_FAILURE: {
      const { params } = action.payload;
      const templatesGroupKey: string = getTemplatesGroupStoreKey(params);
      const newState: IAccountState = {
        ...state,
        checklistTemplates: {
          ...state.checklistTemplates,
          [templatesGroupKey]: {
            ...state.checklistTemplates[templatesGroupKey],
            status: AccountStatusesEnum.FAILED,
          },
        },
      };

      return newState;
    }

    case AccountActionsEnum.ACCOUNT_REQUIREMENT_TEMPLATES_GET_REQUEST: {
      const { params } = action.payload;
      const templatesGroupKey: string = getTemplatesGroupStoreKey(params);
      const newState: IAccountState = {
        ...state,
        requirementTemplates: {
          ...state.requirementTemplates,
          [templatesGroupKey]: {
            ...state.requirementTemplates[templatesGroupKey],
            status: AccountStatusesEnum.LOADING,
          },
        },
      };

      return newState;
    }
    case AccountActionsEnum.ACCOUNT_REQUIREMENT_TEMPLATES_GET_SUCCESS: {
      const { params, data } = action.payload;
      const templatesGroupKey: string = getTemplatesGroupStoreKey(params);
      const newState: IAccountState = {
        ...state,
        requirementTemplates: {
          ...state.requirementTemplates,
          [templatesGroupKey]: {
            ...state.requirementTemplates[templatesGroupKey],
            data,
            status: AccountStatusesEnum.SUCCEEDED,
          },
        },
      };

      return newState;
    }
    case AccountActionsEnum.ACCOUNT_REQUIREMENT_TEMPLATES_GET_FAILURE: {
      const { params } = action.payload;
      const templatesGroupKey: string = getTemplatesGroupStoreKey(params);
      const newState: IAccountState = {
        ...state,
        requirementTemplates: {
          ...state.requirementTemplates,
          [templatesGroupKey]: {
            ...state.requirementTemplates[templatesGroupKey],
            status: AccountStatusesEnum.FAILED,
          },
        },
      };

      return newState;
    }

    case AccountActionsEnum.ACCOUNT_ORGANIZATIONS_WITH_DISTRICT_GET_REQUEST: {
      const newState: IAccountState = {
        ...state,
        organizationsWithDistrict: {
          ...state.organizationsWithDistrict,
          status: AccountStatusesEnum.LOADING,
        },
      };

      return newState;
    }
    case AccountActionsEnum.ACCOUNT_ORGANIZATIONS_GET_REQUEST: {
      const newState: IAccountState = {
        ...state,
        organizations: {
          ...state.organizations,
          status: AccountStatusesEnum.LOADING,
        },
      };

      return newState;
    }
    case AccountActionsEnum.ACCOUNT_ORGANIZATIONS_WITH_DISTRICT_GET_SUCCESS: {
      const { data } = action.payload;
      const newState: IAccountState = {
        ...state,
        organizationsWithDistrict: {
          ...state.organizationsWithDistrict,
          data: {
            kno: data.KNO,
            prosecutor: data.PROSECUTOR,
          },
          status: AccountStatusesEnum.SUCCEEDED,
        },
      };

      return newState;
    }
    case AccountActionsEnum.ACCOUNT_ORGANIZATIONS_GET_SUCCESS: {
      const { data } = action.payload;
      const newState: IAccountState = {
        ...state,
        organizations: {
          data: {
            kno: data.KNO,
            prosecutor: data.PROSECUTOR,
          },
          status: AccountStatusesEnum.SUCCEEDED,
        },
      };

      return newState;
    }
    case AccountActionsEnum.ACCOUNT_ORGANIZATIONS_RESET: {
      const newState: IAccountState = {
        ...state,
        organizations: initialAccountState.organizations,
      };

      return newState;
    }
    case AccountActionsEnum.ACCOUNT_ORGANIZATIONS_WITH_DISTRICT_GET_FAILURE: {
      const newState: IAccountState = {
        ...state,
        organizationsWithDistrict: {
          ...state.organizationsWithDistrict,
          status: AccountStatusesEnum.FAILED,
        },
      };

      return newState;
    }
    case AccountActionsEnum.ACCOUNT_ORGANIZATIONS_GET_FAILURE: {
      const newState: IAccountState = {
        ...state,
        organizations: {
          ...state.organizations,
          status: AccountStatusesEnum.FAILED,
        },
      };

      return newState;
    }

    case AccountActionsEnum.ACCOUNT_KNO_ORGANIZATION_FUNCTIONS_GET_REQUEST: {
      const { knoOrganizationId } = action.payload;
      const knoOrganizationFunctionsGroupKey: string = getKnoOrganizationFunctionsGroupStoreKey(knoOrganizationId);
      const newState: IAccountState = {
        ...state,
        knoOrganizationFunctions: {
          ...state.knoOrganizationFunctions,
          [knoOrganizationFunctionsGroupKey]: {
            ...state.knoOrganizationFunctions[knoOrganizationFunctionsGroupKey],
            status: AccountStatusesEnum.LOADING,
          },
        },
      };

      return newState;
    }
    case AccountActionsEnum.ACCOUNT_KNO_ORGANIZATION_FUNCTIONS_GET_SUCCESS: {
      const { knoOrganizationId, data } = action.payload;
      const knoOrganizationFunctionsGroupKey: string = getKnoOrganizationFunctionsGroupStoreKey(knoOrganizationId);
      const newState: IAccountState = {
        ...state,
        knoOrganizationFunctions: {
          ...state.knoOrganizationFunctions,
          [knoOrganizationFunctionsGroupKey]: {
            ...state.knoOrganizationFunctions[knoOrganizationFunctionsGroupKey],
            data,
            status: AccountStatusesEnum.SUCCEEDED,
          },
        },
      };

      return newState;
    }
    case AccountActionsEnum.ACCOUNT_KNO_ORGANIZATION_FUNCTIONS_GET_FAILURE: {
      const { knoOrganizationId } = action.payload;
      const knoOrganizationFunctionsGroupKey: string = getKnoOrganizationFunctionsGroupStoreKey(knoOrganizationId);
      const newState: IAccountState = {
        ...state,
        knoOrganizationFunctions: {
          ...state.knoOrganizationFunctions,
          [knoOrganizationFunctionsGroupKey]: {
            ...state.knoOrganizationFunctions[knoOrganizationFunctionsGroupKey],
            status: AccountStatusesEnum.FAILED,
          },
        },
      };

      return newState;
    }

    default:
      return state;
  }
}
